/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import MomentPropTypes from 'react-moment-proptypes';
import {
  Timeline, Typography,
} from 'antd';
import styled from 'styled-components';
import { DEFAULT_DATE_TIME, DEFAULT_TIME } from '../../../utils/constants/timeFormats';
import {
  Duration, LoadingIndexedIcon, LoadingUnloadingIndexedIcon, UnloadingIndexedIcon,
} from '../../../components';
import HereMapsLocation from '../../../components/Vehicle/Data/HereMapsLocation';
import StopLocation from './StopLocation';
import ParkingIndexedIcon from '../../../components/Styled/Icons/ParkingIndexed';
import BreakIndexedIcon from '../../../components/Styled/Icons/BreakIndexedIcon';
import UnknownIndexedIcon from '../../../components/Styled/Icons/UnknownIndexedIcon';
import eventBus from '../../../services/EventBus';
import { stopPlacePropTypes } from '../../../types/stop.type';

const DetailsLine = styled(Typography.Paragraph)`
  margin-bottom: 3px !important;
  
  .greyed {
    color: ${({ theme }) => theme.color.gray2}
  }
`;

const Header = styled(DetailsLine)`
  font-size: 14px;
  margin-bottom: 3px !important;
`;

const EventLabel = styled.span`
  font-weight: 500;
`;

function determineEndDateString(t, { start, end }) {
  if (start.isSame(end)) {
    return '';
  }

  if (start.clone().startOf('d').isSame(end.clone().startOf('d'))) {
    return ` – ${end.format(DEFAULT_TIME)}`;
  }

  return ` – ${end.format(DEFAULT_DATE_TIME)}`;
}

const dispatchEventSelected = ({ eventType, eventId }) => eventBus.dispatch('event-selected', { eventType, eventId });

const DotByType = {
  break: (index, eventId) => (
    <BreakIndexedIcon
      index={index}
      onClick={() => dispatchEventSelected({ eventType: 'break', eventId })}
    />
  ),
  parking: (index, eventId) => (
    <ParkingIndexedIcon
      index={index}
      onClick={() => dispatchEventSelected({ eventType: 'break', eventId })}
    />
  ),
  loading: (index, eventId) => (
    <LoadingIndexedIcon
      style={{ paddingTop: '7px', paddingLeft: '7px' }}
      onClick={() => dispatchEventSelected({ eventType: 'stop', eventId })}
      index={index}
    />
  ),
  unloading: (index, eventId) => (
    <UnloadingIndexedIcon
      style={{ paddingTop: '7px', paddingLeft: '7px' }}
      onClick={() => dispatchEventSelected({ eventType: 'stop', eventId })}
      index={index}
    />
  ),
  unknown: (index, eventId) => (
    <UnknownIndexedIcon
      style={{ paddingTop: '7px', paddingLeft: '7px' }}
      onClick={() => dispatchEventSelected({ eventType: 'stop', eventId })}
      index={index}
    />
  ),
  loadingUnloading: (index, eventId) => (
    <LoadingUnloadingIndexedIcon
      style={{ paddingTop: '7px', paddingLeft: '7px' }}
      onClick={() => dispatchEventSelected({ eventType: 'stop', eventId })}
      index={index}
    />
  ),
};

// The result of this lookup is invoked immediately, so a miss on an unrecognised event
// type would throw and take the whole breaks timeline down. Fall back to the UNKNOWN dot.
// An own-property test rather than a nullish check, because DotByType is a plain object
// literal: a type of 'toString' or 'constructor' would otherwise resolve to an inherited
// Object.prototype member and still crash.
const dotFor = (type, index, eventId) => (
  Object.prototype.hasOwnProperty.call(DotByType, type) ? DotByType[type] : DotByType.unknown
)(index, eventId);

const EventToStopType = {
  loading: 'LOADING',
  unloading: 'UNLOADING',
  loadingUnloading: 'LOADING_UNLOADING',
  unknown: 'UNKNOWN',
  operational: 'OPERATIONAL',
  handover: 'HANDOVER',
  waypoint: 'WAYPOINT',
  service: 'SERVICE',
};

/*
 * Guarded for the same reason the dot lookup is, and against the same inputs - it just fails softer.
 * An unmapped event type used to index straight into the table, yielding undefined and rendering the
 * literal string "ORDER.STOP_TYPES.undefined" to the user. It degrades rather than throwing, which
 * is why it was left out of the crash fix, but it is still user-visible garbage.
 * An own-property test, not a nullish check: a type of 'toString' would otherwise resolve to an
 * inherited Object.prototype member.
 */
const stopTypeLabelKey = (type) => {
  const stopType = Object.prototype.hasOwnProperty.call(EventToStopType, type)
    ? EventToStopType[type]
    : EventToStopType.unknown;
  return `ORDER.STOP_TYPES.${stopType}`;
};

/*
 * The stopover behaviour labels (PM-177) reach these components as event types too:
 * BreaksDescriptionsCard.determineStopEventType emits the lower-cased label for a non-operational
 * stop, and EventToStopType above resolves it to the matching translation.
 *
 * The dot itself stays the existing one - DotByType is frozen byte-for-byte by
 * scripts/verify-stop-lookup-fallbacks.mjs, and `dotFor` already falls back safely. No regression:
 * these stops have no load/unload flags, so they resolved to that same dot before.
 */
const STOP_EVENT_TYPES = [
  'loading', 'unloading', 'loadingUnloading', 'unknown',
  'operational', 'handover', 'waypoint', 'service',
];

const BreakEvent = ({ event }) => {
  const { t } = useTranslation();

  return (
    <Timeline.Item dot={dotFor(event.type, event.index, event.id)} key={event.id}>
      <Header>
        <EventLabel>
          {t(`ORDER.BREAKS.TYPE.${event.type.toUpperCase()}`)}
        </EventLabel>
      </Header>
      <DetailsLine>
        <EventLabel>
          {t('ORDER.BREAKS.TIME')}
          {': '}
        </EventLabel>
        {`${event.time.start.format(DEFAULT_DATE_TIME)}${determineEndDateString(t, event.time)}`}
        <br />
        <EventLabel>
          {t('ORDER.BREAKS.DURATION')}
          {': '}
        </EventLabel>
        <Duration value={event.time.duration} />
      </DetailsLine>
      <DetailsLine>
        <HereMapsLocation hereLocation={{ items: [event.location] }} withCoordinates />
      </DetailsLine>
    </Timeline.Item>
  );
};

BreakEvent.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.oneOf(['break', 'parking', ...STOP_EVENT_TYPES]).isRequired,
    index: PropTypes.number.isRequired,
    time: PropTypes.shape({
      start: MomentPropTypes.momentObj.isRequired,
      end: MomentPropTypes.momentObj.isRequired,
      duration: MomentPropTypes.momentDurationObj.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      title: PropTypes.string.isRequired,
      position: PropTypes.shape({
        lat: PropTypes.string.isRequired,
        lng: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

const StopEvent = ({ event }) => {
  const { t } = useTranslation();

  return (
    <Timeline.Item dot={dotFor(event.type, event.index, event.id)} key={event.id}>
      <DetailsLine>
        <EventLabel>
          {t(stopTypeLabelKey(event.type))}
          {': '}
        </EventLabel>
        <StopLocation place={event.location} inline />
      </DetailsLine>
    </Timeline.Item>
  );
};

StopEvent.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    index: PropTypes.number.isRequired,
    type: PropTypes.oneOf(STOP_EVENT_TYPES).isRequired,
    location: stopPlacePropTypes.isRequired,
  }).isRequired,
};

export default {
  Break: BreakEvent,
  Stop: StopEvent,
};
